import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, FormControl, Grid, IconButton, MenuItem, Select, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';

interface AvailabilityCalendarProps {
  availability: { [date: string]: boolean };
  serviceTypes?: string[];
  isOwnProfile?: boolean;
}

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  availability,
  serviceTypes = [],
  isOwnProfile = false,
}) => {
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());
  const [serviceType, setServiceType] = useState(serviceTypes[0] ?? '');

  const handlePrevMonth = () => {
    setCurrentMonth(currentMonth.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth(currentMonth.add(1, 'month'));
  };

  // Get calendar days
  const getCalendarDays = () => {
    const firstDayOfMonth = currentMonth.startOf('month');
    const lastDayOfMonth = currentMonth.endOf('month');
    const startDate = firstDayOfMonth.startOf('week');
    const endDate = lastDayOfMonth.endOf('week');

    const days: Dayjs[] = [];
    let currentDate = startDate;

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      days.push(currentDate);
      currentDate = currentDate.add(1, 'day');
    }

    return days;
  };

  const isAvailable = (date: Dayjs): boolean => {
    const dateString = date.format('YYYY-MM-DD');
    return availability[dateString] ?? false;
  };

  const isCurrentMonth = (date: Dayjs): boolean => {
    return date.month() === currentMonth.month();
  };

  const isToday = (date: Dayjs): boolean => {
    return date.isSame(dayjs(), 'day');
  };

  const calendarDays = getCalendarDays();
  const weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: 2,
        p: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        mb: 3,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Availability
        </Typography>
        {isOwnProfile && (
          <IconButton size="small">
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Service Type Selector */}
      {serviceTypes.length > 0 && (
        <FormControl fullWidth sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, color: '#5A5A5A' }}>
            Service type
          </Typography>
          <Select
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value)}
            startAdornment={<LocationOnIcon sx={{ mr: 1, fontSize: 20, color: '#178B8E' }} />}
            sx={{
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
            }}
          >
            {serviceTypes.map((s) => (
              <MenuItem key={s} value={s}>
                {s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Calendar */}
      <Box>
        {/* Month Navigation */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <IconButton onClick={handlePrevMonth} size="small">
            <NavigateBeforeIcon />
          </IconButton>

          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mx: 3, minWidth: 150, textAlign: 'center' }}
          >
            {currentMonth.format('MMMM YYYY')}
          </Typography>

          <IconButton onClick={handleNextMonth} size="small">
            <NavigateNextIcon />
          </IconButton>
        </Box>

        {/* Weekday Headers */}
        <Grid container spacing={1} sx={{ mb: 1 }}>
          {weekDays.map((day) => (
            <Grid size={{ xs: 12 / 7 }} key={day}>
              <Box
                sx={{
                  textAlign: 'center',
                  py: 1,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#5A5A5A' }}>
                  {day}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Calendar Days */}
        <Grid container spacing={1}>
          {calendarDays.map((date, index) => {
            const available = isAvailable(date);
            const inCurrentMonth = isCurrentMonth(date);
            const today = isToday(date);

            return (
              <Grid size={{ xs: 12 / 7 }} key={index}>
                <Box
                  sx={{
                    aspectRatio: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1,
                    cursor: available && inCurrentMonth ? 'pointer' : 'default',
                    backgroundColor: !inCurrentMonth
                      ? 'transparent'
                      : available
                        ? '#B2E5E7'
                        : '#E8E8E8',
                    border: today ? '2px solid #178B8E' : 'none',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: available && inCurrentMonth ? '#8FD8DB' : undefined,
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: today ? 700 : 400,
                      color: inCurrentMonth ? '#333' : '#B0B0B0',
                    }}
                  >
                    {date.date()}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Legend */}
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            mt: 3,
            justifyContent: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#B2E5E7',
              }}
            />
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Available
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#E8E8E8',
              }}
            />
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Unavailable
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AvailabilityCalendar;
